import React, { useState, useEffect } from "react";
import { ValidationErrors } from "src/errors";
import { AuthFormAction, AuthFormData, AuthFormDataKey } from "src/types";
import { TextInputBox, FormLvlErrorMessage } from "src/components";
import { useNavigate } from "react-router-dom";
import { HEALF_IT_CONTACT_EMAIL } from "src/constants/emails";

interface AuthForgotPasswordSubmitProps {
  formData: AuthFormData;
  validationErrors?: ValidationErrors<AuthFormDataKey>;
  formLvlErrorMessage: string;
  handleInputChange: (field: AuthFormDataKey, value: string) => void;
  setAction: (action: AuthFormAction) => void;
}

const AuthForgotPasswordSubmit: React.FC<AuthForgotPasswordSubmitProps> = ({
  formData,
  validationErrors,
  formLvlErrorMessage,
  handleInputChange,
  setAction
}) => {
  const [resendCount, setResendCount] = useState(0);
  const [resendTimer, setResendTimer] = useState(60);
  const resendDisableButton = resendCount >= 2;

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer((t) => t - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleResendClick = () => {
    setResendCount((prevCount) => prevCount + 1);
    setAction("FORGOT_PASSWORD");
  };

  const navigate = useNavigate();

  return (
    <div className="p-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-secondary-900 mb-2">Reset Password</h2>
        <p className="text-secondary-600">Enter the code and your new password</p>
        <p className="text-secondary-500 text-sm">If an account exists with this email, a reset code has been sent.</p>
        <p className="text-secondary-500 text-sm">It may take few minutes to arrive.</p>
      </div>

      <div className="space-y-6">
        <div>
          <TextInputBox
            id="otp"
            label="Verification Code"
            placeholder="Enter the 6-digit code"
            value={formData.otp}
            onChange={(value: string) => handleInputChange("otp", value)}
            maxLength={6}
            required
            error={validationErrors?.getErrorMessageByFormDataKey("otp")}
          />
        </div>

        <div>
          <TextInputBox
            id="new-password"
            label="New Password"
            placeholder="Enter your new password"
            value={formData.newPassword}
            onChange={(value: string) => handleInputChange("newPassword", value)}
            type="password"
            required
            error={validationErrors?.getErrorMessageByFormDataKey("newPassword")}
          />
        </div>

        <div>
          <TextInputBox
            id="confirm-password"
            label="Confirm Password"
            placeholder="Confirm your new password"
            value={formData.confirmPassword}
            onChange={(value: string) => handleInputChange("confirmPassword", value)}
            type="password"
            required
            error={validationErrors?.getErrorMessageByFormDataKey("confirmPassword")}
          />
        </div>

        <FormLvlErrorMessage message={formLvlErrorMessage} />

        <div className="space-y-4">
          <button
            className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-primary-300 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            disabled={!formData.otp || !formData.newPassword || !formData.confirmPassword}
            type="submit"
          >
            Reset Password
          </button>

          <button
            className="w-full text-primary-600 hover:text-primary-700 disabled:text-primary-300 disabled:cursor-not-allowed font-medium py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            onClick={handleResendClick}
            type="button"
            disabled={resendTimer > 0 || resendDisableButton}
          >
            {resendTimer > 0
              ? `Resend Code in ${resendTimer}s`
              : resendDisableButton
                ? "Resend Code (Max attempts reached)"
                : "Resend Code"}
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            className="flex-1 text-primary-600 hover:text-primary-700 font-medium py-2 px-4 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-0"
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
            }}
            type="button"
          >
            Back to Sign In
          </button>
        </div>

        <div className="border-t border-secondary-200 text-center">
          <p className="text-secondary-500 text-sm">
            If you haven't received the code in 5 minutes or having trouble receiving your code? Contact our support
            team at{" "}
            <a
              href={`mailto:${HEALF_IT_CONTACT_EMAIL}`}
              className="text-primary-600 hover:text-primary-700 underline font-medium"
            >
              {HEALF_IT_CONTACT_EMAIL}
            </a>{" "}
            for assistance.
          </p>
        </div>
      </div>
    </div>
  );
};

export { AuthForgotPasswordSubmit };
